<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="
      emit(
        'update:modelValue',
        $event
      )
    "
  >
    <v-card rounded="xl">
      <!-- ======================================================
           HEADER
      ======================================================= -->

      <v-card-title
        class="d-flex align-center pa-5"
      >
        <v-avatar
          color="primary"
          variant="tonal"
          size="44"
          class="mr-3"
        >
          <v-icon>
            mdi-point-of-sale
          </v-icon>
        </v-avatar>

        <div>
          <div class="text-h6 font-weight-bold">
            Select POS Terminal
          </div>

          <div class="text-caption text-medium-emphasis">
            Choose an active POS terminal for this workstation.
          </div>
        </div>

        <v-spacer />

        <v-btn
          v-if="allowClose"
          icon="mdi-close"
          variant="text"
          :disabled="loading"
          @click="close"
        />
      </v-card-title>

      <v-divider />

      <!-- ======================================================
           BODY
      ======================================================= -->

      <v-card-text class="pa-5">
        <!-- ==================================================
             ERROR
        =================================================== -->

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- ==================================================
             LOADING
        =================================================== -->

        <div
          v-if="loading"
          class="text-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="44"
          />

          <div
            class="text-body-2 text-medium-emphasis mt-3"
          >
            Loading terminals...
          </div>
        </div>

        <!-- ==================================================
             NO ACTIVE TERMINALS
        =================================================== -->

        <div
          v-else-if="activeTerminals.length === 0"
          class="terminal-empty-state"
        >
          <v-avatar
            color="warning"
            variant="tonal"
            size="68"
            class="mb-4"
          >
            <v-icon size="34">
              mdi-monitor-off
            </v-icon>
          </v-avatar>

          <div class="text-h6 font-weight-bold">
            No Active POS Terminal
          </div>

          <div
            class="text-body-2 text-medium-emphasis mt-2"
          >
            No active POS terminal is currently available for this
            tenant.
          </div>

          <div
            class="text-body-2 text-medium-emphasis mt-1"
          >
            An administrator must create or activate a terminal
            before a POS session can be started.
          </div>
        </div>

        <!-- ==================================================
             ACTIVE TERMINALS
        =================================================== -->

        <v-radio-group
          v-else
          v-model="selectedTerminalId"
          hide-details
        >
          <v-card
            v-for="terminal in activeTerminals"
            :key="getTerminalId(terminal)"
            variant="outlined"
            rounded="lg"
            class="terminal-option mb-3"
            :class="{
              selected:
                String(selectedTerminalId) ===
                String(getTerminalId(terminal))
            }"
            @click="
              selectedTerminalId =
                getTerminalId(terminal)
            "
          >
            <div class="d-flex align-center pa-4">
              <!-- RADIO -->

              <v-radio
                :value="getTerminalId(terminal)"
                hide-details
              />

              <!-- ICON -->

              <v-avatar
                color="primary"
                variant="tonal"
                size="42"
                class="mx-3"
              >
                <v-icon>
                  {{
                    terminal.isMobile
                      ? "mdi-tablet"
                      : terminal.isSelfService
                        ? "mdi-monitor-dashboard"
                        : "mdi-monitor"
                  }}
                </v-icon>
              </v-avatar>

              <!-- DETAILS -->

              <div class="flex-grow-1 terminal-details">
                <div
                  class="text-subtitle-2 font-weight-bold"
                >
                  {{
                    terminal.terminalCode
                    ||
                    terminal.code
                    ||
                    "POS Terminal"
                  }}
                </div>

                <div
                  class="text-body-2 text-medium-emphasis"
                >
                  {{
                    terminal.name
                    ||
                    terminal.terminalName
                    ||
                    "Point of Sale Terminal"
                  }}
                </div>

                <!-- LOCATION -->

                <div
                  v-if="getLocationName(terminal)"
                  class="terminal-meta mt-2"
                >
                  <v-icon
                    size="14"
                    class="mr-1"
                  >
                    mdi-map-marker-outline
                  </v-icon>

                  <span>
                    {{ getLocationName(terminal) }}
                  </span>

                  <span
                    v-if="getLocationCode(terminal)"
                    class="ml-1"
                  >
                    · {{ getLocationCode(terminal) }}
                  </span>
                </div>

                <!-- DEVICE -->

                <div
                  v-if="terminal.deviceName"
                  class="terminal-meta mt-1"
                >
                  <v-icon
                    size="14"
                    class="mr-1"
                  >
                    mdi-devices
                  </v-icon>

                  {{ terminal.deviceName }}
                </div>

                <!-- MODE -->

                <div class="d-flex flex-wrap ga-1 mt-2">
                  <v-chip
                    size="x-small"
                    variant="tonal"
                  >
                    {{
                      terminal.isSelfService
                        ? "Self Service"
                        : "Assisted"
                    }}
                  </v-chip>

                  <v-chip
                    v-if="terminal.isMobile"
                    size="x-small"
                    color="info"
                    variant="tonal"
                  >
                    Mobile
                  </v-chip>
                </div>
              </div>

              <!-- STATUS -->

              <v-chip
                color="success"
                variant="tonal"
                size="small"
              >
                <v-icon
                  start
                  size="14"
                >
                  mdi-check-circle-outline
                </v-icon>

                Active
              </v-chip>
            </div>
          </v-card>
        </v-radio-group>
      </v-card-text>

      <v-divider />

      <!-- ======================================================
           ACTIONS
      ======================================================= -->

      <v-card-actions class="pa-5">
        <v-btn
          v-if="allowClose"
          variant="outlined"
          prepend-icon="mdi-close"
          :disabled="loading"
          @click="close"
        >
          Close
        </v-btn>

        <v-spacer />

        <v-btn
          v-if="activeTerminals.length > 0"
          color="primary"
          prepend-icon="mdi-check-circle-outline"
          :loading="loading"
          :disabled="!selectedTerminalId"
          @click="confirm"
        >
          Use This Terminal
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

// ============================================================
// PROPS
// ============================================================

const props =
  defineProps({
    modelValue: {
      type: Boolean,
      default: false
    },

    terminals: {
      type: Array,
      default: () => []
    },

    currentTerminalId: {
      type: [
        String,
        Number,
        null
      ],
      default: null
    },

    loading: {
      type: Boolean,
      default: false
    },

    error: {
      type: String,
      default: ""
    },

    allowClose: {
      type: Boolean,
      default: true
    }
  });

// ============================================================
// EVENTS
// ============================================================

const emit =
  defineEmits([
    "update:modelValue",
    "select"
  ]);

// ============================================================
// STATE
// ============================================================

const selectedTerminalId =
  ref(null);

// ============================================================
// ACTIVE TERMINALS
// ============================================================

const activeTerminals =
  computed(() => {
    const terminals =
      Array.isArray(
        props.terminals
      )
        ? props.terminals
        : [];

    return terminals.filter(
      terminal =>
        terminal?.isActive === true
    );
  });

// ============================================================
// TERMINAL ID
// ============================================================

function getTerminalId(
  terminal
) {
  return (
    terminal?.id
    ??
    terminal?.terminalId
    ??
    null
  );
}

// ============================================================
// LOCATION NAME
// ============================================================

function getLocationName(
  terminal
) {
  return (
    terminal?.terminalLocationName
    ??
    terminal?.locationName
    ??
    ""
  );
}

// ============================================================
// LOCATION CODE
// ============================================================

function getLocationCode(
  terminal
) {
  return (
    terminal?.terminalLocationCode
    ??
    terminal?.locationCode
    ??
    ""
  );
}

// ============================================================
// CONFIRM
// ============================================================

function confirm() {
  if (
    !selectedTerminalId.value
  ) {
    return;
  }

  const terminal =
    activeTerminals.value.find(
      item =>
        String(
          getTerminalId(
            item
          )
        ) ===
        String(
          selectedTerminalId.value
        )
    );

  if (!terminal) {
    return;
  }

  /*
   * Selecting a terminal does NOT open a POS session.
   *
   * The layout/header must still require the operator
   * to deliberately choose Start Session.
   */
  emit(
    "select",
    terminal
  );
}

// ============================================================
// CLOSE
// ============================================================

function close() {
  if (
    props.loading
  ) {
    return;
  }

  emit(
    "update:modelValue",
    false
  );
}

// ============================================================
// INITIAL SELECTION
// ============================================================

watch(
  () =>
    props.modelValue,

  opened => {
    if (!opened) {
      selectedTerminalId.value =
        null;

      return;
    }

    const currentId =
      props.currentTerminalId;

    const currentIsAvailable =
      activeTerminals.value.some(
        terminal =>
          String(
            getTerminalId(
              terminal
            )
          ) ===
          String(
            currentId
          )
      );

    /*
     * Preserve the currently selected terminal only when
     * it remains active and available.
     */
    if (
      currentId != null &&
      currentIsAvailable
    ) {
      selectedTerminalId.value =
        currentId;

      return;
    }

    selectedTerminalId.value =
      null;

    /*
     * If exactly one active terminal exists, preselect it
     * for convenience.
     *
     * IMPORTANT:
     * This still does not activate/use it automatically.
     * The user must click "Use This Terminal".
     */
    if (
      activeTerminals.value.length === 1
    ) {
      selectedTerminalId.value =
        getTerminalId(
          activeTerminals.value[0]
        );
    }
  },
  {
    immediate: true
  }
);

// ============================================================
// TERMINAL LIST CHANGES WHILE DIALOG IS OPEN
// ============================================================

watch(
  activeTerminals,

  terminals => {
    if (
      !props.modelValue
    ) {
      return;
    }

    if (
      selectedTerminalId.value != null
    ) {
      const selectionStillExists =
        terminals.some(
          terminal =>
            String(
              getTerminalId(
                terminal
              )
            ) ===
            String(
              selectedTerminalId.value
            )
        );

      if (
        selectionStillExists
      ) {
        return;
      }
    }

    selectedTerminalId.value =
      terminals.length === 1
        ? getTerminalId(
            terminals[0]
          )
        : null;
  }
);
</script>

<style scoped>
/* ============================================================
   TERMINAL OPTION
============================================================ */

.terminal-option {
  cursor: pointer;

  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.terminal-option:hover {
  background:
    rgba(
      127,
      127,
      127,
      0.05
    );
}

.terminal-option.selected {
  border-color:
    rgb(
      var(--v-theme-primary)
    );

  background:
    rgba(
      var(--v-theme-primary),
      0.06
    );
}

/* ============================================================
   TERMINAL DETAILS
============================================================ */

.terminal-details {
  min-width: 0;
}

.terminal-meta {
  display: flex;
  align-items: center;

  min-width: 0;

  font-size: 12px;

  color:
    rgba(
      var(--v-theme-on-surface),
      0.6
    );
}

/* ============================================================
   EMPTY STATE
============================================================ */

.terminal-empty-state {
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  text-align: center;

  padding: 28px 16px;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 599px) {
  .terminal-option :deep(.v-card) {
    width: 100%;
  }

  .terminal-option .v-chip {
    display: none;
  }
}
</style>